import store from "../store";

// Tracks the last known input state bitmask from the headset.
// Used to detect bit transitions (0→1, 1→0) rather than reacting to absolute values.
let previousInputState = 0;

// Timestamp of the last output report sent to the headset via operateHeadset().
// Input reports arriving within a short window after an output are treated as echoes
// (the headset reflecting the state we just told it to enter) and are silently consumed.
let lastOutputReportTimestamp = 0;
const ECHO_SUPPRESSION_MS = 300;

// Yealink HID input report bitmask (Section 5.1 of the Yealink WebHID documentation):
//   Bit 0: hookswitch   — 1 = off-hook (call active), 0 = on-hook (idle/hung up)
//   Bit 1: linebusytone  — 1 = answering state
//   Bit 2: phonemute     — 1 = muted
//   Bit 3: flash         — 1 = hold/flash
//   Bit 6: programbtn    — 1 = reject call
const BIT_HOOKSWITCH  = 0x01; // bit 0
const BIT_LINEBUSY    = 0x02; // bit 1
const BIT_PHONEMUTE   = 0x04; // bit 2
const BIT_FLASH       = 0x08; // bit 3
const BIT_PROGRAMBTN  = 0x40; // bit 6

const handleInputReport = event => {
	const { data, reportId } = event;
	const storedReportId = store.getters["voice/headsetDeviceReportId"];

	// Only process reports from the telephony HID collection
	if (reportId !== storedReportId) return;

	const value = data.getUint8(0);
	const keypadValue = ((data.getUint8(0) & 0x80) >> 7) | ((data.getUint8(1) & 0x07) << 1);

	const activeCall = store.getters["voice/activeCall"];
	const isOnCall = store.getters["voice/isOnCall"];
	const hasIncomingCall = store.getters["voice/hasIncomingCall"];

	// Parse individual bitfields from the current state
	const hookswitch = (value & BIT_HOOKSWITCH) !== 0;
	const phonemute  = (value & BIT_PHONEMUTE)  !== 0;
	const flash      = (value & BIT_FLASH)       !== 0;
	const programbtn = (value & BIT_PROGRAMBTN)  !== 0;

	// Parse previous state
	const prevHookswitch = (previousInputState & BIT_HOOKSWITCH) !== 0;
	const prevMute       = (previousInputState & BIT_PHONEMUTE)  !== 0;
	const prevFlash      = (previousInputState & BIT_FLASH)       !== 0;

	console.debug(
		`[Headset] Input report: value=${value} (hook=${hookswitch}, mute=${phonemute}, flash=${flash}, reject=${programbtn}, keypad=${keypadValue}), ` +
		`prev=${previousInputState}, reportId=${reportId}`
	);

	// Suppress echoes: when we send an output report the headset echoes back
	// the new state. Without suppression this would trigger duplicate actions.
	const now = Date.now();

	if (now - lastOutputReportTimestamp < ECHO_SUPPRESSION_MS) {
		console.debug("[Headset] Suppressing input report (echo from recent output command)");
		previousInputState = value;
		return;
	}

	// --- Hookswitch transitions ---

	// Off-hook transition (0→1): user picked up / pressed hook to answer
	if (hookswitch && !prevHookswitch) {
		if (hasIncomingCall && activeCall) {
			console.debug("[Headset] Answering incoming call (hookswitch 0→1)");
			activeCall.accept();
		} else {
			console.debug("[Headset] Off-hook detected (hookswitch 0→1), no pending incoming call");
		}
	}

	// On-hook transition (1→0): user hung up / pressed hook to end call
	if (!hookswitch && prevHookswitch) {
		if (isOnCall && activeCall) {
			console.debug("[Headset] Hanging up call (hookswitch 1→0)");
			activeCall.hangUp();
			store.commit("voice/disconnectCall");
			if (window.mediaStream) {
				window.mediaStream.getTracks().forEach(track => track.stop());
			}
		} else {
			console.debug("[Headset] On-hook detected (hookswitch 1→0), no active call to hang up");
		}
	}

	// Mute is handled entirely in hardware by the headset — no software action needed.

	// --- Flash/hold transition (0→1): user pressed hold ---
	if (flash && !prevFlash) {
		if (activeCall) {
			if (activeCall.onHold) {
				console.debug("[Headset] Resuming call from hold (flash 0→1)");
				activeCall.resume();
				operateHeadset(true, false, false, false);
			} else {
				console.debug("[Headset] Putting call on hold (flash 0→1)");
				activeCall.hold();
				operateHeadset(false, false, false, true);
			}
		}
	}

	// --- Reject (programbtn) ---
	if (programbtn) {
		if (hasIncomingCall && activeCall) {
			console.debug("[Headset] Rejecting incoming call (programbtn)");
			activeCall.reject();
		}
	}

	// Update tracked state for next comparison
	previousInputState = value;
};

export async function addHeadsetEventListeners(headset) {
	if (headset) {
		try {
			await headset.open();
			console.debug("[Headset] Device opened successfully.");

			// Default to reportId 2 per Yealink specification; override if
			// the device's telephony collection advertises a different one.
			let reportId = 2;

			headset.collections.forEach(collection => {
				collection.inputReports.forEach(inputReport => {
					if (collection.usage === 0x0005 && collection.usagePage === 0x000B) {
						reportId = inputReport.reportId;
						console.debug(`[Headset] Report ID discovered from collection: ${reportId}`);
					}
				});
			});
			store.commit("voice/setHeadsetDeviceReportId", reportId);
			console.debug(`[Headset] Report ID set: ${reportId}`);

			headset.oninputreport = handleInputReport;

			// Force the headset into a known idle state. After a page reload the
			// headset may still think it is off-hook from the previous session.
			// Without this reset, the first inbound ring command (value 4) is
			// interpreted as a multi-channel event and the call gets dropped.
			if (reportId) {
				lastOutputReportTimestamp = Date.now();
				await headset.sendReport(reportId, new Uint8Array([0]));
				console.debug("[Headset] Sent idle/reset command (value=0) on init");
			}
		} catch (err) {
			console.error(`[Headset] Error setting up headset event listeners: ${err}`);
		}
	} else {
		console.error("[Headset] Headset device not found.");
	}
}

/**
 * Operates the headset by sending control commands via WebHID.
 *
 * @param {boolean} hook - Set to true to go off-hook, false to go on-hook.
 * @param {boolean} mute - Set to true to mute the headset, false to unmute.
 * @param {boolean} ringing - Set to true to enable ringing, false to disable.
 * @param {boolean} holding - Set to true to hold the call, false to resume.
 */
export async function operateHeadset(hook = false, mute = false, ringing = false, holding = false) {
	const headset = store.getters["voice/headsetDevice"];
	const reportId = store.getters["voice/headsetDeviceReportId"];

	if (headset) {
		const value = new Uint8Array([
			(hook ? 1 : 0) |
            (mute ? 2 : 0) |
            (ringing ? 4 : 0) |
            (holding ? 8 : 0)
		]);

		try {
			console.debug(`[Headset] Sending output report: value=${value[0]} (hook=${hook}, mute=${mute}, ring=${ringing}, hold=${holding}), reportId=${reportId}`);

			// Record timestamp so the input handler can suppress the headset's echo
			lastOutputReportTimestamp = Date.now();

			await headset.sendReport(reportId, value);
		} catch (err) {
			console.debug(`[Headset] Error sending headset command (report): ${err}`);
		}
	} else {
		console.debug("[Headset] Headset device not found.");
	}
}

/**
 * Resets the internal headset state tracking. Call this when the headset
 * is disconnected or forgotten so stale state doesn't affect future sessions.
 */
export function resetHeadsetState() {
	previousInputState = 0;
	lastOutputReportTimestamp = 0;
}
